import {} from "@plotlibre/core";
import { BatchEditCommand } from "./batch-edit-command.js";
import { SelectionController, } from "./selection-controller.js";
const EFFECTIVE_COORDINATE_EPSILON_DEGREES = 1e-12;
export class SelectionTransformCommandError extends Error {
    code;
    featureIds;
    cause;
    constructor(code, message, options = {}) {
        super(message);
        this.name = "SelectionTransformCommandError";
        this.code = code;
        this.featureIds = Object.freeze([...(options.featureIds ?? [])]);
        this.cause = options.cause;
    }
}
/**
 * Builds one stale-safe atomic command from a preflighted transform completion.
 *
 * Members whose authored controls remain effectively at the shared pivot are
 * not replaced and retain their original revision. The coordinate epsilon only
 * absorbs local projection roundoff; exact Store-state validation remains in
 * force for every feature that the command actually observes.
 */
export function createSelectionTransformCommand(store, selection, options) {
    const originals = options.completion.originals;
    const transformed = options.completion.transformed;
    validateCompletionPair(options.completion.kind, originals, transformed);
    validateSelectionMembership(options.selectionSnapshot, originals);
    const replacements = [];
    const undoReplacements = [];
    const actualPostState = [];
    for (const [index, original] of originals.entries()) {
        const next = transformed[index];
        if (sameControlPointsWithinTransformEpsilon(original, next)) {
            actualPostState.push(original);
            continue;
        }
        replacements.push(next);
        undoReplacements.push(original);
        actualPostState.push(next);
    }
    if (replacements.length === 0)
        return undefined;
    const orderedIds = Object.freeze(store.list().map((feature) => feature.id));
    const inner = new BatchEditCommand(store, selection, {
        label: options.completion.kind === "rotate"
            ? "rotate-selection"
            : "scale-selection",
        execute: {
            replace: replacements,
            orderedIds,
        },
        undo: {
            replace: undoReplacements,
            orderedIds,
        },
        beforeSelection: options.selectionSnapshot,
        afterSelection: options.selectionSnapshot,
    });
    return new ValidatedSelectionTransformCommand(store, selection, inner, options.completion.kind, originals, actualPostState, orderedIds, options.selectionSnapshot);
}
class ValidatedSelectionTransformCommand {
    label;
    #store;
    #selection;
    #inner;
    #kind;
    #originals;
    #actualPostState;
    #orderedIds;
    #selectionSnapshot;
    constructor(store, selection, inner, kind, originals, actualPostState, orderedIds, selectionSnapshot) {
        this.label = inner.label;
        this.#store = store;
        this.#selection = selection;
        this.#inner = inner;
        this.#kind = kind;
        this.#originals = originals;
        this.#actualPostState = actualPostState;
        this.#orderedIds = orderedIds;
        this.#selectionSnapshot = selectionSnapshot;
    }
    execute() {
        this.#assertExpectedState(this.#originals, "execute");
        this.#inner.execute();
    }
    undo() {
        this.#assertExpectedState(this.#actualPostState, "undo");
        this.#inner.undo();
    }
    #assertExpectedState(expectedFeatures, operation) {
        assertSelectionSemantics(this.#selection.snapshot(), this.#selectionSnapshot, this.#kind, operation);
        for (const expected of expectedFeatures) {
            const current = this.#store.find(expected.id);
            if (current === undefined) {
                throw new SelectionTransformCommandError("SELECTION_TRANSFORM_FEATURE_MISSING", `Cannot ${operation} ${this.#kind} transform because feature "${expected.id}" is missing.`, { featureIds: [expected.id] });
            }
            if (!sameFeature(current, expected)) {
                throw new SelectionTransformCommandError("SELECTION_TRANSFORM_TRANSACTION_INVALID", `Cannot ${operation} ${this.#kind} transform because feature "${expected.id}" changed after preview.`, { featureIds: [expected.id] });
            }
        }
        const currentOrder = this.#store.list().map((feature) => feature.id);
        if (!sameIds(currentOrder, this.#orderedIds)) {
            throw new SelectionTransformCommandError("SELECTION_TRANSFORM_TRANSACTION_INVALID", `Cannot ${operation} ${this.#kind} transform because Store document order changed.`, { featureIds: expectedFeatures.map((feature) => feature.id) });
        }
    }
}
function validateCompletionPair(kind, originals, transformed) {
    if (originals.length === 0 || originals.length !== transformed.length) {
        throw new SelectionTransformCommandError("SELECTION_TRANSFORM_TRANSACTION_INVALID", `${kind} transform completion must contain matching non-empty before/after feature sets.`, { featureIds: originals.map((feature) => feature.id) });
    }
    for (const [index, original] of originals.entries()) {
        const next = transformed[index];
        if (next.id !== original.id) {
            throw new SelectionTransformCommandError("SELECTION_TRANSFORM_TRANSACTION_INVALID", `${kind} transform cannot change feature ids or feature ordering.`, { featureIds: [original.id, next.id] });
        }
        if (next.plotType !== original.plotType ||
            next.definitionVersion !== original.definitionVersion ||
            next.revision !== original.revision + 1 ||
            !sameJson(next.parameters, original.parameters) ||
            !sameJson(next.style, original.style) ||
            !sameJson(next.metadata, original.metadata)) {
            throw new SelectionTransformCommandError("SELECTION_TRANSFORM_TRANSACTION_INVALID", `${kind} transform may change only authored controls and increment revision exactly once.`, { featureIds: [original.id] });
        }
    }
}
function validateSelectionMembership(snapshot, originals) {
    const featureIds = new Set(originals.map((feature) => feature.id));
    if (featureIds.size !== originals.length ||
        snapshot.selectedIds.length !== originals.length ||
        snapshot.selectedIds.some((id) => !featureIds.has(id))) {
        throw new SelectionTransformCommandError("SELECTION_TRANSFORM_TRANSACTION_INVALID", "Selection transform completion must cover the complete captured selection.", { featureIds: originals.map((feature) => feature.id) });
    }
}
function assertSelectionSemantics(current, expected, kind, operation) {
    if (!sameIds(current.selectedIds, expected.selectedIds) ||
        current.primaryId !== expected.primaryId) {
        throw new SelectionTransformCommandError("SELECTION_TRANSFORM_TRANSACTION_INVALID", `Cannot ${operation} ${kind} transform because selection membership or Primary changed.`, { featureIds: expected.selectedIds });
    }
}
function sameFeature(left, right) {
    return (left.id === right.id &&
        left.plotType === right.plotType &&
        left.definitionVersion === right.definitionVersion &&
        left.revision === right.revision &&
        sameControlPointsExact(left, right) &&
        sameJson(left.parameters, right.parameters) &&
        sameJson(left.style, right.style) &&
        sameJson(left.metadata, right.metadata));
}
function sameControlPointsWithinTransformEpsilon(left, right) {
    return (left.controlPoints.length === right.controlPoints.length &&
        left.controlPoints.every((position, index) => {
            const candidate = right.controlPoints[index];
            return (candidate !== undefined &&
                Math.abs(candidate[0] - position[0]) <=
                    EFFECTIVE_COORDINATE_EPSILON_DEGREES &&
                Math.abs(candidate[1] - position[1]) <=
                    EFFECTIVE_COORDINATE_EPSILON_DEGREES);
        }));
}
function sameControlPointsExact(left, right) {
    return (left.controlPoints.length === right.controlPoints.length &&
        left.controlPoints.every((position, index) => {
            const candidate = right.controlPoints[index];
            return (candidate !== undefined &&
                candidate[0] === position[0] &&
                candidate[1] === position[1]);
        }));
}
function sameJson(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
}
function sameIds(left, right) {
    return (left.length === right.length &&
        left.every((id, index) => id === right[index]));
}
//# sourceMappingURL=selection-transform-command.js.map